import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Import the main language store to sync with it
import { useLanguageStore as useMainLanguageStore } from '../../quiz-core/languageStore';

// Enhanced language interface for Phase 5
export interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  direction: 'ltr' | 'rtl';
  audioSupported: boolean;
  completionPercentage: number;
  qualityScore: number;
  regions: string[];
  ttsVoice?: string;
  fontFamily?: string;
}

export interface OfflineContentPack {
  language: string;
  version: string;
  size: number;
  downloadedAt: Date;
  questions: number;
  categories: number;
  audioFiles: number;
}

export interface AudioSettings {
  enabled: boolean;
  autoPlay: boolean;
  playbackSpeed: number; // 0.5 to 2.0
  voice: string;
  downloadWifiOnly: boolean;
}

export interface LanguageState {
  // Current language settings
  currentLanguage: string;
  previousLanguage: string;
  
  // Available languages with metadata
  availableLanguages: Language[];
  downloadedLanguages: string[];
  audioLanguages: string[];
  rtlLanguages: string[];
  
  // Quality and completion tracking
  translationQuality: Record<string, number>;
  completionStatus: Record<string, number>;
  
  // Offline content management
  offlineContent: Record<string, OfflineContentPack>;
  downloadQueue: string[];
  isDownloading: boolean;
  downloadProgress: Record<string, number>;
  
  // Audio settings and state
  audioSettings: AudioSettings;
  audioCache: Record<string, string>; // questionId -> audioUrl
  
  // Usage analytics
  languageUsageStats: Record<string, {
    sessionsCount: number;
    totalDuration: number;
    lastUsed: Date;
  }>;
  
  // Actions
  switchLanguage: (languageCode: string) => Promise<void>;
  downloadLanguagePack: (language: string, priority?: 'high' | 'medium' | 'low') => Promise<void>;
  removeLanguagePack: (language: string) => Promise<void>;
  updateAudioSettings: (settings: Partial<AudioSettings>) => void;
  trackLanguageUsage: (language: string, feature: string, duration: number) => void;
  getLanguageDirection: (language?: string) => 'ltr' | 'rtl';
  isLanguageRTL: (language?: string) => boolean;
  getLanguageMetadata: (language: string) => Language | undefined;
  updateTranslationQuality: (language: string, quality: number) => void;
  clearCache: () => void;
}

// Supported languages with enhanced metadata
const SUPPORTED_LANGUAGES: Language[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    direction: 'ltr',
    audioSupported: true,
    completionPercentage: 100,
    qualityScore: 5.0,
    regions: ['north_america', 'europe', 'oceania'],
    ttsVoice: 'en-US-Neural2-A',
    fontFamily: 'System'
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    flag: '🇪🇸',
    direction: 'ltr',
    audioSupported: true,
    completionPercentage: 85,
    qualityScore: 4.2,
    regions: ['latin_america', 'europe'],
    ttsVoice: 'es-ES-Neural2-A',
    fontFamily: 'System'
  },
  {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷',
    direction: 'ltr',
    audioSupported: true,
    completionPercentage: 90,
    qualityScore: 4.5,
    regions: ['europe', 'africa'],
    ttsVoice: 'fr-FR-Neural2-A',
    fontFamily: 'System'
  },
  {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    direction: 'ltr',
    audioSupported: true,
    completionPercentage: 75,
    qualityScore: 4.0,
    regions: ['europe'],
    ttsVoice: 'de-DE-Neural2-A',
    fontFamily: 'System'
  },
  {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    flag: '🇧🇷',
    direction: 'ltr',
    audioSupported: true,
    completionPercentage: 80,
    qualityScore: 4.1,
    regions: ['latin_america', 'europe'],
    ttsVoice: 'pt-BR-Neural2-A',
    fontFamily: 'System'
  },
  {
    code: 'ru',
    name: 'Russian',
    nativeName: 'Русский',
    flag: '🇷🇺',
    direction: 'ltr',
    audioSupported: true,
    completionPercentage: 70,
    qualityScore: 3.8,
    regions: ['europe', 'asia'],
    ttsVoice: 'ru-RU-Neural2-A',
    fontFamily: 'System'
  },
  {
    code: 'tl',
    name: 'Tagalog',
    nativeName: 'Filipino',
    flag: '🇵🇭',
    direction: 'ltr',
    audioSupported: true,
    completionPercentage: 65,
    qualityScore: 3.5,
    regions: ['asia'],
    ttsVoice: 'fil-PH-Standard-A',
    fontFamily: 'System'
  },
  {
    code: 'it',
    name: 'Italian',
    nativeName: 'Italiano',
    flag: '🇮🇹',
    direction: 'ltr',
    audioSupported: true,
    completionPercentage: 75,
    qualityScore: 4.0,
    regions: ['europe'],
    ttsVoice: 'it-IT-Neural2-A',
    fontFamily: 'System'
  },
  {
    code: 'am',
    name: 'Amharic',
    nativeName: 'አማርኛ',
    flag: '🇪🇹',
    direction: 'ltr',
    audioSupported: false,
    completionPercentage: 60,
    qualityScore: 3.2,
    regions: ['africa'],
    fontFamily: 'NotoSansEthiopic'
  },
  {
    code: 'ko',
    name: 'Korean',
    nativeName: '한국어',
    flag: '🇰🇷',
    direction: 'ltr',
    audioSupported: true,
    completionPercentage: 70,
    qualityScore: 3.8,
    regions: ['asia'],
    ttsVoice: 'ko-KR-Neural2-A',
    fontFamily: 'System'
  }
];

const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentLanguage: 'en',
      previousLanguage: 'en',
      availableLanguages: SUPPORTED_LANGUAGES,
      downloadedLanguages: ['en'],
      audioLanguages: [],
      rtlLanguages: RTL_LANGUAGES,
      translationQuality: {},
      completionStatus: {},
      offlineContent: {},
      downloadQueue: [],
      isDownloading: false,
      downloadProgress: {},
      audioSettings: {
        enabled: true,
        autoPlay: false,
        playbackSpeed: 1.0,
        voice: 'neural',
        downloadWifiOnly: true
      },
      audioCache: {},
      languageUsageStats: {},

      // Switch language with enhanced features
      switchLanguage: async (languageCode: string) => {
        const { currentLanguage, trackLanguageUsage } = get();
        
        if (currentLanguage === languageCode) return;

        try {
          // Track language switch
          trackLanguageUsage(languageCode, 'switch', 1);
          
          // Update state
          set({
            previousLanguage: currentLanguage,
            currentLanguage: languageCode
          });
          
          // Sync with main language store
          useMainLanguageStore.getState().setLanguage(languageCode);

          // Download language pack if not available
          const { downloadedLanguages, downloadLanguagePack } = get();
          if (!downloadedLanguages.includes(languageCode)) {
            await downloadLanguagePack(languageCode, 'high');
          }

          // Update UI direction for RTL languages
          // This would trigger UI re-render with proper direction
          
        } catch (error) {
          console.error('Failed to switch language:', error);
          throw error;
        }
      },

      // Download language pack with progress tracking
      downloadLanguagePack: async (language: string, priority = 'medium') => {
        const { downloadQueue, isDownloading } = get();
        
        if (downloadQueue.includes(language) || isDownloading) {
          return;
        }

        set({
          downloadQueue: [...downloadQueue, language],
          isDownloading: true,
          downloadProgress: { ...get().downloadProgress, [language]: 0 }
        });

        try {
          // Simulate download progress (replace with actual implementation)
          for (let progress = 0; progress <= 100; progress += 10) {
            await new Promise(resolve => setTimeout(resolve, 100));
            set({
              downloadProgress: { ...get().downloadProgress, [language]: progress }
            });
          }

          // Mark as downloaded
          const { downloadedLanguages, offlineContent } = get();
          const newPack: OfflineContentPack = {
            language,
            version: '1.0.0',
            size: 1024 * 1024 * 5, // 5MB
            downloadedAt: new Date(),
            questions: 500,
            categories: 10,
            audioFiles: 100
          };

          set({
            downloadedLanguages: [...downloadedLanguages, language],
            offlineContent: { ...offlineContent, [language]: newPack },
            downloadQueue: downloadQueue.filter(l => l !== language),
            isDownloading: false
          });

        } catch (error) {
          console.error('Failed to download language pack:', error);
          set({
            downloadQueue: downloadQueue.filter(l => l !== language),
            isDownloading: false
          });
          throw error;
        }
      },

      // Remove language pack
      removeLanguagePack: async (language: string) => {
        if (language === 'en') {
          throw new Error('Cannot remove English language pack');
        }

        const { downloadedLanguages, offlineContent, audioCache } = get();
        
        // Remove from downloaded languages
        const newDownloadedLanguages = downloadedLanguages.filter(l => l !== language);
        
        // Remove offline content
        const newOfflineContent = { ...offlineContent };
        delete newOfflineContent[language];
        
        // Clear audio cache for this language
        const newAudioCache = { ...audioCache };
        Object.keys(newAudioCache).forEach(key => {
          if (key.includes(`_${language}`)) {
            delete newAudioCache[key];
          }
        });

        set({
          downloadedLanguages: newDownloadedLanguages,
          offlineContent: newOfflineContent,
          audioCache: newAudioCache
        });
      },

      // Update audio settings
      updateAudioSettings: (settings: Partial<AudioSettings>) => {
        set({
          audioSettings: { ...get().audioSettings, ...settings }
        });
      },

      // Track language usage for analytics
      trackLanguageUsage: (language: string, feature: string, duration: number) => {
        const { languageUsageStats } = get();
        const currentStats = languageUsageStats[language] || {
          sessionsCount: 0,
          totalDuration: 0,
          lastUsed: new Date()
        };

        set({
          languageUsageStats: {
            ...languageUsageStats,
            [language]: {
              sessionsCount: currentStats.sessionsCount + 1,
              totalDuration: currentStats.totalDuration + duration,
              lastUsed: new Date()
            }
          }
        });
      },

      // Get language direction
      getLanguageDirection: (language?: string) => {
        const lang = language || get().currentLanguage;
        const langData = SUPPORTED_LANGUAGES.find(l => l.code === lang);
        return langData?.direction || 'ltr';
      },

      // Check if language is RTL
      isLanguageRTL: (language?: string) => {
        const lang = language || get().currentLanguage;
        return RTL_LANGUAGES.includes(lang);
      },

      // Get language metadata
      getLanguageMetadata: (language: string) => {
        return SUPPORTED_LANGUAGES.find(l => l.code === language);
      },

      // Update translation quality
      updateTranslationQuality: (language: string, quality: number) => {
        set({
          translationQuality: {
            ...get().translationQuality,
            [language]: quality
          }
        });
      },

      // Clear all cached data
      clearCache: () => {
        set({
          audioCache: {},
          downloadProgress: {},
          languageUsageStats: {}
        });
      }
    }),
    {
      name: 'language-store',
      storage: {
        getItem: async (name) => {
          const value = await AsyncStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: async (name, value) => {
          await AsyncStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: async (name) => {
          await AsyncStorage.removeItem(name);
        }
      },
      // Only persist essential data (exclude functions)
      partialize: (state) => ({
        currentLanguage: state.currentLanguage,
        previousLanguage: state.previousLanguage,
        availableLanguages: state.availableLanguages,
        downloadedLanguages: state.downloadedLanguages,
        audioLanguages: state.audioLanguages,
        rtlLanguages: state.rtlLanguages,
        translationQuality: state.translationQuality,
        completionStatus: state.completionStatus,
        offlineContent: state.offlineContent,
        downloadQueue: state.downloadQueue,
        isDownloading: state.isDownloading,
        downloadProgress: state.downloadProgress,
        audioSettings: state.audioSettings,
        audioCache: state.audioCache,
        languageUsageStats: state.languageUsageStats
      }) as LanguageState
    }
  )
);

// Helper hooks for common operations
export const useCurrentLanguage = () => {
  return useLanguageStore(state => state.currentLanguage);
};

export const useLanguageDirection = () => {
  return useLanguageStore(state => state.getLanguageDirection());
};

export const useIsRTL = () => {
  return useLanguageStore(state => state.isLanguageRTL());
};

export const useAudioSettings = () => {
  return useLanguageStore(state => state.audioSettings);
};

// Sync with the main language store
// This ensures the enhanced store stays in sync with the main store
let syncInitialized = false;
if (!syncInitialized) {
  syncInitialized = true;
  
  // Subscribe to main store changes
  useMainLanguageStore.subscribe((state) => {
    const currentLang = useLanguageStore.getState().currentLanguage;
    if (state.language !== currentLang) {
      console.log(`🔄 Syncing enhanced store: ${currentLang} -> ${state.language}`);
      useLanguageStore.setState({ 
        currentLanguage: state.language,
        previousLanguage: currentLang 
      });
    }
  });
  
  // Initialize with current main store value
  const mainLanguage = useMainLanguageStore.getState().language;
  useLanguageStore.setState({ 
    currentLanguage: mainLanguage,
    previousLanguage: mainLanguage 
  });
  console.log(`🎯 Enhanced store initialized with: ${mainLanguage}`);
}

export const useLanguageMetadata = (language?: string) => {
  return useLanguageStore(state => {
    const lang = language || state.currentLanguage;
    return state.getLanguageMetadata(lang);
  });
};